/**
 * Auth-flavoured TanStack Query hooks.
 *
 * `useMe` is the single gating signal for authenticated routes — its
 * success state means "the API recognises the session cookie" and a 401
 * means "redirect to /login". No JWT decoding happens on the client.
 *
 * Login / signup / logout are mutations that piggy-back on `useMe`'s
 * cache: they invalidate / overwrite the `me` cache entry so the auth
 * guard reacts immediately without an extra round-trip.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AuthUser, LoginInput, SignupInput } from "@ottopilot/shared";
import { ApiClientError, apiGet, apiPost } from "../lib/api";
import { queryKeys } from "./queryKeys";

type MeResponse = { user: AuthUser };

/**
 * Resolves the current user. The API returns `{ user }` when authenticated
 * and 401 (`UNAUTHENTICATED`) otherwise. Components consume `data?.user`
 * — the gating happens via the `RequireAuth` wrapper which inspects
 * `error?.status === 401` to redirect.
 */
export function useMe() {
  return useQuery<MeResponse, ApiClientError>({
    queryKey: queryKeys.auth.me,
    queryFn: () => apiGet<MeResponse>("/api/auth/me"),
    // Don't retry on 401 — that's a deliberate "you are signed out" signal.
    retry: (failureCount, error) => {
      if (error instanceof ApiClientError && error.status === 401) return false;
      return failureCount < 1;
    },
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation<MeResponse, ApiClientError, LoginInput>({
    mutationFn: (input) => apiPost<MeResponse>("/api/auth/login", input),
    onSuccess: (data) => {
      // Seed the me cache so the next render sees the authed user without
      // a refetch. Subsequent views still benefit from background refresh.
      qc.setQueryData(queryKeys.auth.me, data);
    },
  });
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation<MeResponse, ApiClientError, SignupInput>({
    mutationFn: (input) => apiPost<MeResponse>("/api/auth/signup", input),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.auth.me, data);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, void>({
    mutationFn: () => apiPost<{ ok: true }>("/api/auth/logout"),
    onSuccess: () => {
      // Drop every cache entry — anything keyed on the old session is
      // stale by definition.
      qc.clear();
    },
  });
}
